import { createContext, useContext, useState, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import logger from '../utils/logger';

const DownloadContext = createContext(null);

/**
 * Provider for managing file download state across the application
 */
export const DownloadProvider = ({ children }) => {
  const [activeDownloads, setActiveDownloads] = useState(new Map());
  
  // Ref to always access latest activeDownloads in getter functions
  // This prevents getter functions from being recreated on every state update
  const activeDownloadsRef = useRef(activeDownloads);
  
  // Keep ref in sync with state
  // Use useLayoutEffect to ensure synchronous updates before cleanup effects run
  useLayoutEffect(() => {
    activeDownloadsRef.current = activeDownloads;
  }, [activeDownloads]);

  /**
   * Start a new download
   * @param {string} messageId - Unique identifier for the download
   * @param {Object} metadata - Download metadata
   * @param {string} metadata.filename - Name of the file
   * @param {string} metadata.mimeType - MIME type
   * @param {AbortController} metadata.abortController - Controller for cancellation
   */
  const startDownload = useCallback((messageId, metadata) => {
    logger.debug('Starting download:', { messageId, filename: metadata.filename });
    
    setActiveDownloads(prev => {
      const updated = new Map(prev);
      updated.set(messageId, {
        ...metadata,
        loadedBytes: 0,
        totalBytes: null,
        percent: null, // Start with null, will update to actual percent once known
        status: 'downloading',
        startTime: Date.now(),
        displayMode: 'inline', // 'inline' or 'modal'
      });
      return updated;
    });
  }, []);

  /**
   * Update download progress
   * @param {string} messageId - Download identifier
   * @param {Object} progress - Progress data
   * @param {number} progress.loaded - Bytes downloaded
   * @param {number} progress.total - Total bytes (null if unknown)
   * @param {number} progress.percent - Percentage (null if unknown)
   */
  const updateProgress = useCallback((messageId, { loaded, total, percent }) => {
    setActiveDownloads(prev => {
      const updated = new Map(prev);
      const download = updated.get(messageId);
      
      if (download) {
        updated.set(messageId, {
          ...download,
          loadedBytes: loaded,
          totalBytes: total,
          percent: percent !== undefined ? percent : download.percent,
        });
      }
      
      return updated;
    });
  }, []);

  /**
   * Escalate download from inline to modal display
   * (Used for large files that take >5 seconds)
   * @param {string} messageId - Download identifier
   */
  const escalateToModal = useCallback((messageId) => {
    logger.debug('Escalating download to modal:', messageId);
    
    setActiveDownloads(prev => {
      const updated = new Map(prev);
      const download = updated.get(messageId);
      
      if (download && download.displayMode === 'inline') {
        updated.set(messageId, { 
          ...download, 
          displayMode: 'modal', 
        });
      }
      
      return updated;
    });
  }, []);

  /**
   * Cancel an active download
   * @param {string} messageId - Download identifier
   */
  const cancelDownload = useCallback((messageId) => {
    setActiveDownloads(prev => {
      const updated = new Map(prev);
      const download = updated.get(messageId);
      
      if (download) {
        logger.debug('Cancelling download:', { messageId, filename: download.filename });
        
        // Abort the fetch request
        if (download.abortController) {
          download.abortController.abort();
        }
        
        // Remove from map
        updated.delete(messageId);
      }
      
      return updated;
    });
  }, []);

  /**
   * Mark download as completed and remove from state
   * @param {string} messageId - Download identifier
   */
  const completeDownload = useCallback((messageId) => {
    logger.debug('Completing download:', messageId);
    
    setActiveDownloads(prev => {
      const updated = new Map(prev);
      updated.delete(messageId);
      return updated;
    });
  }, []);

  /**
   * Get download state for a specific message
   * @param {string} messageId - Download identifier
   * @returns {Object|null} Download state or null if not found
   */
  const getDownloadState = useCallback((messageId) => {
    return activeDownloadsRef.current.get(messageId) || null;
  }, []); // No dependencies - reads from ref, stable reference

  /**
   * Check if any downloads are currently active
   * @returns {boolean}
   */
  const hasActiveDownloads = useCallback(() => {
    return activeDownloadsRef.current.size > 0;
  }, []); // No dependencies - reads from ref, stable reference

  /**
   * Get all downloads in modal display mode
   * @returns {Array} Array of [messageId, downloadState] pairs
   */
  const getModalDownloads = useCallback(() => {
    return Array.from(activeDownloadsRef.current.entries())
      .filter(([_, download]) => download.displayMode === 'modal');
  }, []); // No dependencies - reads from ref, stable reference

  // Memoize the context value to prevent unnecessary re-renders
  // Only recreate when activeDownloads changes (all functions are stable)
  const value = useMemo(() => ({
    activeDownloads,
    activeDownloadsRef, // Expose ref for accessing latest state without triggering re-renders
    startDownload,
    updateProgress,
    escalateToModal,
    cancelDownload,
    completeDownload,
    getDownloadState,
    hasActiveDownloads,
    getModalDownloads,
  }), [
    activeDownloads,
    // All functions below are stable (no dependencies), so they don't cause re-creation
    startDownload,
    updateProgress,
    escalateToModal,
    cancelDownload,
    completeDownload,
    getDownloadState,
    hasActiveDownloads,
    getModalDownloads,
  ]);

  return (
    <DownloadContext.Provider value={value}>
      {children}
    </DownloadContext.Provider>
  );
};

DownloadProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access download context for a specific message
 * @param {string} messageId - Optional message ID to get specific download state
 * @returns {Object} Download context methods and state
 */
export const useDownload = (messageId = null) => {
  const context = useContext(DownloadContext);
  
  if (!context) {
    throw new Error('useDownload must be used within DownloadProvider');
  }
  
  // If messageId provided, include specific download state
  // Memoize to prevent creating new object on every render
  const downloadState = messageId ? context.getDownloadState(messageId) : null;
  
  return useMemo(() => {
    if (messageId) {
      return {
        ...context,
        downloadState,
      };
    }
    return context;
  }, [context, messageId, downloadState]);
};

export default DownloadContext;
